import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "@dtci/spellbook/Typography";
import TwoRowsLayout from "tap/components/TwoRowsLayout";
import { savePromoListDefaultFilters } from "ppt/api";
import { usePPTActions, usePPTState } from "tap/store/ppt/usePPT";
import PPTToastNotificationProvider from "../components/PPTToastNotifications";
import { usePPTToasts } from "tap/pages/PPT/hooks/usePPTToasts";
import { usePPTAuthorization } from "tap/pages/PPT/hooks/usePPTAuthorization";
import { usePromoListData } from "tap/pages/PPT/hooks/usePromoListData";
import { usePromoListUrlState } from "tap/pages/PPT/hooks/usePromoListUrlState";
import Skeleton from "@dtci/spellbook/Skeleton";
import "../styles.scss";
import styles from "./pptHomePage.module.scss";
import { PPTPromosGrid } from "../components/PPTPromosGrid";
import { PPTAdminActionsMenu } from "../components/PPTAdminActionsMenu";
import { PPTHomeSearchHeader } from "./PPTHomeSearchHeader";
import { useAgGridApi, useAgGridUrlState } from "tap/components/AGGrid/hooks.js";
import {
  buildSearchString,
  parseSearchParams,
  updateSearchParams,
} from "tap/utils/urlParams";
import {
  GRID_PREFERENCES_STATUS,
  PROMO_LIST_GRID_PARAMS,
  PROMO_LIST_URL_PARAMS,
} from "ppt/constants";
import { isPromoTileFilter } from "ppt/utilities";
import { persistBeforeNavTo } from "tap/components/PageNavigation/navStorage";

const PPTHomePageContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [gridControls, setGridControls] = useState(null);
  const { showSuccessToast, showErrorToast } = usePPTToasts();
  const { hasPptPermission, isPptAdmin } = usePPTAuthorization();
  const { loading, hasLoadedOnce } = usePromoListData(hasPptPermission);
  const {
    gridStateRef,
    searchRef,
    tileFilterRef,
    locationRef,
    writeUrl,
    debouncedWriteUrl,
    lastWrittenSearchRef,
    buildCurrentParams,
  } = usePromoListUrlState();

  const { setPromosSearch, setPromosIssueFilter, updateGridPreferences } =
    usePPTActions();
  const { gridPreferences, gridPreferencesStatus, promosSearch, promosIssueFilter } = usePPTState();

  const preferencesReady =
    gridPreferencesStatus === GRID_PREFERENCES_STATUS.LOADED;

  const agGridApi = useAgGridApi();
  const gridRef = agGridApi.AgGridApiRef;

  const writeGridState = useCallback(
    (state) => {
      gridStateRef.current = state;
      writeUrl();
    },
    [gridStateRef, writeUrl],
  );

  const { initialState, onStateUpdated } = useAgGridUrlState(
    location.search,
    PROMO_LIST_GRID_PARAMS,
    writeGridState,
  );

  // Seed the grid-state ref from the URL
  useEffect(() => {
    if (initialState) gridStateRef.current = initialState;
  }, [initialState, gridStateRef]);

  // Redirect unauthorized users
  useEffect(() => {
    if (!hasPptPermission) {
      navigate("/home", { replace: true });
    }
  }, [hasPptPermission, navigate]);

  const onSearch = useCallback(
    (search) => {
      setPromosSearch(search);
      searchRef.current = search;
      debouncedWriteUrl.current();
    },
    [setPromosSearch, searchRef, debouncedWriteUrl],
  );

  const onClearAllFilters = useCallback(() => {
    const api = gridRef.current?.api;
    api?.setFilterModel(null);
    api?.applyColumnState({ defaultState: { sort: null } });

    setPromosIssueFilter(null);
    setPromosSearch("");
    searchRef.current = "";
    tileFilterRef.current = null;
    gridStateRef.current = null;
    lastWrittenSearchRef.current = null;

    debouncedWriteUrl.current.cancel();
    writeUrl();
  }, [
    gridRef,
    setPromosIssueFilter,
    setPromosSearch,
    searchRef,
    tileFilterRef,
    gridStateRef,
    lastWrittenSearchRef,
    debouncedWriteUrl,
    writeUrl,
  ]);

  const onResetFilters = useCallback(() => {
    const savedDefaults = gridPreferences?.promoListDefaultFilters;

    // If no saved defaults, clear all filters
    if (!savedDefaults || Object.keys(savedDefaults).length === 0) {
      onClearAllFilters();
      return;
    }

    const api = gridRef.current?.api;
    if (!api) return;

    // Parse saved defaults to extract all filter components
    const params = parseSearchParams(buildSearchString(savedDefaults));
    const urlSearch = params[PROMO_LIST_URL_PARAMS.SEARCH];
    const urlTile = params[PROMO_LIST_URL_PARAMS.TILE];
    const urlSort = params[PROMO_LIST_URL_PARAMS.SORT];
    const urlFilter = params[PROMO_LIST_URL_PARAMS.FILTER];

    const nextSearch = typeof urlSearch === "string" ? urlSearch : "";
    const nextTile = isPromoTileFilter(urlTile) ? urlTile : null;

    // Update search and tile filter refs/Redux
    searchRef.current = nextSearch;
    tileFilterRef.current = nextTile;
    if (nextSearch !== promosSearch) setPromosSearch(nextSearch);
    if (nextTile !== (promosIssueFilter ?? null)) setPromosIssueFilter(nextTile);

    // Apply grid sort state
    if (urlSort) {
      const sortModel = urlSort.split(',').map(col => {
        const desc = col.startsWith('-');
        return {
          colId: desc ? col.substring(1) : col,
          sort: desc ? 'desc' : 'asc',
        };
      });
      api.applyColumnState({ state: sortModel, defaultState: { sort: null } });
    } else {
      api.applyColumnState({ defaultState: { sort: null } });
    }

    // Apply grid filter state
    if (urlFilter) {
      try {
        const filterModel = JSON.parse(urlFilter);
        api.setFilterModel(filterModel);
      } catch (err) {
        console.error('Failed to parse filter model:', err);
        api.setFilterModel(null);
      }
    } else {
      api.setFilterModel(null);
    }

    // Update gridStateRef so URL stays in sync
    gridStateRef.current = {
      sort: urlSort || null,
      filter: urlFilter || null,
    };

    // Navigate to update URL
    const nextSearchString = buildSearchString(savedDefaults);
    lastWrittenSearchRef.current = nextSearchString;
    updateSearchParams(locationRef.current, navigate, savedDefaults);
  }, [
    gridPreferences,
    onClearAllFilters,
    gridRef,
    searchRef,
    tileFilterRef,
    promosSearch,
    promosIssueFilter,
    setPromosSearch,
    setPromosIssueFilter,
    gridStateRef,
    lastWrittenSearchRef,
    locationRef,
    navigate,
  ]);

  const onLeaveForDetail = useCallback(
    (locationRef) => {
      const { pathname, search } = locationRef.current;
      persistBeforeNavTo(pathname, search, "/ppt");
    },
    [],
  );

  const handleResetColumns = useCallback(async () => {
    try {
      await gridControls?.resetColumnState();
    } catch {
      showErrorToast("Failed to reset grid layout. Please try again.");
    }
  }, [gridControls, showErrorToast]);

  const canResetColumns = Boolean(
    gridControls?.isGridReady && gridControls?.hasSavedColumnState,
  );

  const handleSaveDefaultFilters = useCallback(async () => {
    const filters = {
      ...parseSearchParams(buildSearchString(buildCurrentParams())),
    };
    try {
      await savePromoListDefaultFilters(filters);
      updateGridPreferences({ promoListDefaultFilters: filters });
      showSuccessToast("Default filters saved.");
    } catch (err) {
      console.error("Error saving default filters:", err);
      showErrorToast("Failed to save default filters. Please try again.");
    }
  }, [
    buildCurrentParams,
    updateGridPreferences,
    showSuccessToast,
    showErrorToast,
  ]);

  if (!hasPptPermission) {
    return null;
  }

  return (
    <section className="ppt-page-container">
      <div className={styles.pageHeader}>
        <div className={styles.pageTitles}>
          <Typography variant="T50" className="ppt-page-title">
            Promo Campaigns
          </Typography>
          <Typography variant="T20" className="ppt-page-subtitle">
            Track and manage promotional campaigns across their lifecycle
          </Typography>
        </div>
        {isPptAdmin && <PPTAdminActionsMenu />}
      </div>

      <TwoRowsLayout>
        <main data-position="top" className="ppt-card">
          <PPTHomeSearchHeader
            gridRef={gridRef}
            onSearchChange={onSearch}
            onResetFilters={onResetFilters}
            onClearAllFilters={onClearAllFilters}
            onSaveDefaultFilters={handleSaveDefaultFilters}
            onResetColumns={handleResetColumns}
            canResetColumns={canResetColumns}
          />
        </main>
        <main data-position="bottom">
          <div className={styles.promosGridContainer}>
            <Skeleton
              isLoading={(loading && !hasLoadedOnce) || !preferencesReady}
            >
              <PPTPromosGrid
                gridRef={gridRef}
                initialState={initialState}
                onStateUpdated={onStateUpdated}
                onLeaveForDetail={() => onLeaveForDetail(locationRef)}
                onColumnStateChange={setGridControls}
              />
            </Skeleton>
          </div>
        </main>
      </TwoRowsLayout>
      <PPTToastNotificationProvider />
    </section>
  );
};

// Applies the user's saved default filters when they arrive on the list with an
// empty query string, by writing them into the URL before the page mounts. The
// page then seeds from the URL exactly as if the user had followed that link,
// so none of its URL handling needs to know defaults exist.
//
// Checked once, on arrival only: after that the page owns the URL, and "Clear
// All Filters" leaving it empty must not bring the defaults back.
export const PPTHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gridPreferences, gridPreferencesStatus } = usePPTState();
  // The query string the page should mount with, decided once preferences load.
  const [arrivalSearch, setArrivalSearch] = useState(null);
  const [isArrivalResolved, setIsArrivalResolved] = useState(false);

  const preferencesReady =
    gridPreferencesStatus === GRID_PREFERENCES_STATUS.LOADED;

  useEffect(() => {
    if (!preferencesReady || arrivalSearch !== null) return;

    const defaults = gridPreferences?.promoListDefaultFilters;
    const hasDefaults = defaults && Object.keys(defaults).length > 0;
    const nextSearch =
      !location.search && hasDefaults ? buildSearchString(defaults) : location.search;

    if (nextSearch !== location.search) {
      navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }
    setArrivalSearch(nextSearch);
  }, [preferencesReady, arrivalSearch, gridPreferences, location, navigate]);

  // Waits for the router to actually report the new URL rather than assuming the
  // navigate above lands in the same render, since the page snapshots
  // location.search on mount.
  useEffect(() => {
    if (!isArrivalResolved && arrivalSearch === location.search) {
      setIsArrivalResolved(true);
    }
  }, [isArrivalResolved, arrivalSearch, location.search]);

  if (!isArrivalResolved) {
    return (
      <section className="ppt-page-container">
        <Skeleton isLoading />
      </section>
    );
  }

  return <PPTHomePageContent />;
};
